/**
 * Shared top navigation bar — used across ALL pages.
 *
 * Adapts based on auth state:
 * - Logged out: marketing nav (How it works, Behind, Pricing, FAQ) + Sign in / CTA
 * - Logged in: app nav (Dashboard, New Analysis, Batch Screening) + user dropdown
 */

import { useState, useRef, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Menu, X, ChevronDown, Settings, LogOut, ArrowLeftRight,
} from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useEntitlements } from '../hooks/useEntitlements'
import { useActiveRole } from '../hooks/useActiveRole'
import { useCreditGate } from '../hooks/useCreditGate'
import OutOfCreditsModal from './OutOfCreditsModal'
import logoImage from '../assets/fittohire-logo.svg'
import chrome from '@site-chrome'

// Header links come from config/site_chrome.json, which the Astro site (blog,
// role pages) reads too. They were two hand-kept lists and drifted.
const toNavLink = (l: { label: string; href: string; spa: boolean; chrome_glyph?: boolean }) => ({
  label: l.label, to: l.href, external: !l.spa, chrome_glyph: !!l.chrome_glyph,
})
const PUBLIC_LINKS = chrome.header.links.map(toNavLink)
const SIGNED_IN_SITE_LINKS = chrome.header.links.filter((l) => !l.signed_out_only).map(toNavLink)

// Three display tiers: Standard / Pro / Recruiter.
// Based on subscription tier (what the user is paying for), NOT persona
// (who they identified as at signup). A recruiter-persona user who
// downgrades their subscription to Pro should see "Pro" immediately.
function tierDisplay(planTier: string): { label: string; cls: string } {
  if (planTier === 'recruiter') return { label: 'Recruiter', cls: 'bg-[#0D3B66] text-white' }
  if (['pro', 'power', 'enterprise'].includes(planTier)) {
    return { label: 'Pro', cls: 'bg-emerald-600 text-white' }
  }
  return { label: 'Standard', cls: 'bg-gray-200 text-gray-700' }
}

export default function TopNav() {
  const { user, isAuthenticated, isPro, planTier, logout } = useAuth()
  const { isAffiliate } = useEntitlements()
  const { setActiveRole, isRecruiterMode } = useActiveRole()
  const location = useLocation()
  const navigate = useNavigate()
  const { navigateWithCreditCheck, showModal, setShowModal } = useCreditGate(isPro)
  const tier = tierDisplay(planTier)
  // Header badge reflects IDENTITY: affiliate shows "Affiliate" (unless they're
  // actively in recruiter mode). The dropdown badge stays the billing plan.
  const onAffiliate = location.pathname === '/affiliate'
  const headerBadge = isAffiliate && !isRecruiterMode
    ? { label: 'Affiliate', cls: 'bg-[#3D7A5A] text-white' }
    : tier
  const [mobileOpen, setMobileOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  // Close user dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false)
    setUserMenuOpen(false)
  }, [location.pathname])

  const handleLogout = async () => {
    // Recruiters → recruiter marketing, everyone else → home. logout() clears the active
    // role, so there's no lingering "recruiter mode" to bounce us back to the dashboard.
    const dest = isRecruiterMode ? '/recruiters' : '/'
    await logout()
    navigate(dest, { replace: true })
  }

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/'
    }
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  // Role-aware app nav. A user can hold a recruiter subscription AND
  // still want their personal job-seeker dashboard (they apply to jobs
  // too). The active role determines which menu they see; the toggle
  // pill switches between them.
  //
  // Batch access still gates on SUBSCRIPTION tier (what they pay for).
  // Persona / activeRole is just the UI mode.
  // A "recruiter" by identity (persona) OR subscription tier. Only recruiters
  // see the in-account-menu role switch + recruiter menus; candidates' nav is
  // untouched. Menus follow the ACTIVE role so the header matches the dashboard.
  const isRecruiter = user?.persona === 'recruiter' || planTier === 'recruiter'
  // The logo is the deliberate door back to your front page, by identity:
  // recruiter → /recruiters, affiliate → /affiliates, candidate → /home, out → /.
  const logoTarget = !isAuthenticated
    ? '/'
    : isRecruiterMode ? '/recruiters'
    : isAffiliate ? '/affiliates'
    : '/home'
  const appLinks = isRecruiterMode
    ? [
        { label: 'Open positions', to: '/dashboard/recruiter' },
        { label: 'Applicants', to: '/dashboard/recruiter/applicants' },
        { label: 'Bulk bloat check', to: '/recruiters/bulk-bloat' },
      ]
    : [{ label: 'Dashboard', to: '/dashboard' }]

  const siteLinks = SIGNED_IN_SITE_LINKS

  // Recruiters at work shouldn't see the marketing chrome — their
  // tool is the product. Mixed-use users (job seekers) still see the
  // marketing links since they're in pre-purchase / dual mode.
  // Marketing pages remain reachable by direct URL.
  const showSiteLinks = !(isAuthenticated && isRecruiterMode)
  const links = isAuthenticated
    ? (showSiteLinks ? [...appLinks, ...siteLinks] : appLinks)
    : PUBLIC_LINKS
  const initials = user?.first_name
    ? user.first_name.charAt(0).toUpperCase()
    : user?.email?.charAt(0).toUpperCase() || '?'
  const displayName = user?.first_name
    ? `${user.first_name}${user.last_name ? ' ' + user.last_name : ''}`
    : user?.email?.split('@')[0] || 'User'

  return (
    <nav className="sticky top-0 z-50 border-b border-gray-200/50 bg-white/80 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* ── Logo ── */}
          <Link
            to={logoTarget}
            className="flex-shrink-0"
          >
            <img src={logoImage} alt="FitToHire" className="h-10" />
          </Link>

          {/* ── Desktop nav links ── */}
          <div className="hidden md:flex items-center gap-1">
            {links.map((link) => {
              const disabled = 'disabled' in link && link.disabled
              const cls = `inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                disabled
                  ? 'text-gray-300 cursor-not-allowed'
                  : isActive(link.to)
                    ? 'text-[#0D3B66] bg-[#0D3B66]/5'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`
              const inner = (
                <>
                  {link.label}
                  {'chrome_glyph' in link && link.chrome_glyph && <ChromeGlyph />}
                  {disabled && (
                    <span className="text-[9px] uppercase tracking-wider text-gray-400 bg-gray-100 px-1 py-px rounded">
                      soon
                    </span>
                  )}
                </>
              )
              if (disabled) {
                return <span key={link.to} className={cls} title="Coming soon">{inner}</span>
              }
              if ('external' in link && link.external) {
                return <a key={link.to} href={link.to} className={cls}>{inner}</a>
              }
              if ('gated' in link && link.gated) {
                return (
                  <button
                    key={link.to}
                    onClick={() => navigateWithCreditCheck(link.to)}
                    className={cls}
                  >
                    {inner}
                  </button>
                )
              }
              return <Link key={link.to} to={link.to} className={cls}>{inner}</Link>
            })}
          </div>

          {/* ── Desktop right side ── */}
          <div className="hidden md:flex items-center gap-3">
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-[#0D3B66] flex items-center justify-center text-xs font-semibold text-white">
                    {initials}
                  </div>
                  <span className="text-sm font-medium text-gray-700 max-w-[120px] truncate">
                    {displayName}
                  </span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${headerBadge.cls}`}>
                    {headerBadge.label}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-gray-400 transition-transform ${
                      userMenuOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {/* Dropdown — plain CSS for the show/hide; no JS animation
                    library needed for a 150ms fade. */}
                {userMenuOpen && (
                  <div
                    className="absolute right-0 mt-2 w-56 bg-white rounded-xl border border-gray-200 shadow-lg shadow-black/5 py-1 z-50 origin-top-right animate-[topnav-pop_140ms_ease-out_forwards]"
                  >
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user?.email}
                      </p>
                      <Link to="/pricing" className="text-xs text-gray-500 mt-0.5 hover:text-[#0D3B66] transition-colors inline-flex items-center gap-1">
                        <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${tier.cls}`}>
                          {tier.label}
                        </span>
                        <span>plan</span>
                      </Link>
                    </div>
                    {isRecruiter && (
                      <button
                        onClick={() => {
                          const next = isRecruiterMode ? 'job_seeker' : 'recruiter'
                          setActiveRole(next)
                          navigate(next === 'recruiter' ? '/dashboard/recruiter' : '/dashboard')
                          setUserMenuOpen(false)
                        }}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <ArrowLeftRight className="w-4 h-4 text-gray-400" />
                        {isRecruiterMode ? 'Switch to job seeking' : 'Switch to recruiting'}
                      </button>
                    )}
                    {isAffiliate && (
                      <button
                        onClick={() => {
                          navigate(onAffiliate ? '/dashboard' : '/affiliate')
                          setUserMenuOpen(false)
                        }}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <ArrowLeftRight className="w-4 h-4 text-gray-400" />
                        {onAffiliate ? 'Back to my dashboard' : 'Switch to affiliate dashboard'}
                      </button>
                    )}
                    <Link
                      to="/dashboard/settings"
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Settings className="w-4 h-4 text-gray-400" />
                      Settings
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4 text-gray-400" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to={chrome.header.sign_in.href}
                  className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors px-3 py-2"
                >
                  {chrome.header.sign_in.label}
                </Link>
                <Link
                  to={chrome.header.cta.href}
                  className="px-4 py-2.5 border-2 border-[#3D7A5A] text-[#3D7A5A] text-sm font-semibold rounded-lg hover:bg-[#3D7A5A] hover:text-white transition-colors"
                >
                  {chrome.header.cta.label}
                </Link>
              </>
            )}
          </div>

          {/* ── Mobile hamburger ── */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* ── Mobile menu — plain CSS slide. max-h transition matches the
              200ms framer-motion animation we're replacing.  ── */}
        {mobileOpen && (
            <div
              className="md:hidden overflow-hidden border-t border-gray-100 animate-[topnav-slide_200ms_ease-out_forwards]"
            >
              <div className="py-3 space-y-1">
                {links.map((link) => {
                  const cls = `block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.to)
                      ? 'text-[#0D3B66] bg-[#0D3B66]/5'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`
                  if ('external' in link && link.external) {
                    return <a key={link.to} href={link.to} className={cls}>{link.label}</a>
                  }
                  if ('gated' in link && link.gated) {
                    return (
                      <button key={link.to} onClick={() => navigateWithCreditCheck(link.to)} className={`${cls} w-full text-left`}>
                        {link.label}
                      </button>
                    )
                  }
                  return <Link key={link.to} to={link.to} className={cls}>{link.label}</Link>
                })}

                <div className="pt-3 border-t border-gray-100 space-y-1">
                  {isAuthenticated ? (
                    <>
                      <div className="px-4 py-2 text-xs text-gray-400 truncate">
                        {user?.email}
                      </div>
                      {isRecruiter && (
                        <button
                          onClick={() => {
                            const next = isRecruiterMode ? 'job_seeker' : 'recruiter'
                            setActiveRole(next)
                            navigate(next === 'recruiter' ? '/dashboard/recruiter' : '/dashboard')
                          }}
                          className="w-full text-left block px-4 py-3 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
                        >
                          {isRecruiterMode ? 'Switch to job seeking' : 'Switch to recruiting'}
                        </button>
                      )}
                      {isAffiliate && (
                        <button
                          onClick={() => navigate(onAffiliate ? '/dashboard' : '/affiliate')}
                          className="w-full text-left block px-4 py-3 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
                        >
                          {onAffiliate ? 'Back to my dashboard' : 'Switch to affiliate dashboard'}
                        </button>
                      )}
                      <Link
                        to="/dashboard/settings"
                        className="block px-4 py-3 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
                      >
                        Settings
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-3 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
                      >
                        Sign out
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to={chrome.header.sign_in.href}
                        className="block px-4 py-3 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
                      >
                        {chrome.header.sign_in.label}
                      </Link>
                      <Link
                        to={chrome.header.cta.href}
                        className="block mx-4 py-3 bg-[#3D7A5A] text-white text-sm font-semibold rounded-lg text-center"
                      >
                        {chrome.header.cta.label}
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
      </div>
      <OutOfCreditsModal open={showModal} onClose={() => setShowModal(false)} />
    </nav>
  )
}

function ChromeGlyph() {
  return (
    <svg viewBox="0 0 48 48" className="w-3.5 h-3.5" aria-hidden="true">
      <path fill="#fff" d="M24 14a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"/>
      <path fill="#ea4335" d="M24 4a20 20 0 0 1 17.32 10H24a10 10 0 0 0-8.66 5L8.68 10.9A19.95 19.95 0 0 1 24 4z"/>
      <path fill="#fbbc04" d="M4 24c0-4.35 1.4-8.37 3.78-11.65l7.04 12.19a10 10 0 0 0 5.18 8.66L13.35 44A20 20 0 0 1 4 24z"/>
      <path fill="#34a853" d="M24 44a20 20 0 0 0 19.32-14.95L29.17 29.43a10 10 0 0 1-15.82 14.57L24 44z"/>
      <circle cx="24" cy="24" r="6" fill="#1a73e8"/>
    </svg>
  )
}
